import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  /** 决定使用原型的 `.rule-table` 还是 `.task-table`（列宽与内边距不同）。 */
  variant?: "rule" | "task";
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  caption?: string;
}

/**
 * 原型基元：`.table-scroll` + `.rule-table` / `.task-table`。
 * 只做结构复用，单元格内容由 `columns.render` 决定。
 */
export function DataTable<T>({
  variant = "rule",
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  caption,
}: DataTableProps<T>) {
  return (
    <div className="table-scroll">
      <table className={variant === "rule" ? "rule-table" : "task-table"}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.className}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>{empty ?? "暂无数据"}</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {columns.map((column) => (
                  <td key={column.key} className={cn(column.className)}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
